import React from "react";
import css from "./dashboard_skeleton.module.scss";

/* ─────────────────────────────────────────────────────────────
   DashboardSkeleton
   Mirrors the exact layout of Dashboard.jsx with shimmer
   placeholder shapes for every section.
   ───────────────────────────────────────────────────────────── */

function Bone({ className, style }) {
  return (
    <div
      className={`${css.skeleton} ${className || ""}`}
      style={style}
    />
  );
}

/* ── Individual section skeletons ──────────────────────────── */

function TitleBarSkeleton() {
  return (
    <div className={css.skTitleWrap}>
      {/* Greeting + datetime */}
      <div className={css.skGreeting}>
        <Bone className={css.skTitle} style={{ width: 260 }} />
        <Bone className={css.skText} style={{ width: 200 }} />
      </div>
      {/* Icon buttons */}
      <div className={css.skIcons}>
        <Bone className={`${css.skCircle} ${css.skIconCircle}`} />
        <Bone className={`${css.skCircle} ${css.skIconCircle}`} />
        <Bone className={`${css.skCircle} ${css.skIconCircle}`} />
      </div>
    </div>
  );
}

function CarouselSkeleton() {
  return <Bone className={css.skBanner} />;
}

function InfoCardsSkeleton() {
  return (
    <div className={css.skCardRow}>
      <Bone className={css.skCard} />
      <Bone className={css.skCard} />
      <Bone className={css.skCard} />
      <Bone className={css.skCard} />
    </div>
  );
}

function TabSectionSkeleton({ courseCount = 3 }) {
  return (
    <div className={css.skTabSection}>
      {/* Tab labels */}
      <Bone className={css.skTab} />
      {/* Course card row */}
      <div className={css.skCourseRow}>
        {Array.from({ length: courseCount }).map((_, i) => (
          <Bone key={i} className={css.skCourse} />
        ))}
      </div>
    </div>
  );
}

function AssessmentSkeleton() {
  return (
    <div className={css.skAssessmentSection}>
      {/* Section title */}
      <Bone className={css.skText} style={{ width: 180 }} />
      {/* Assessment cards grid */}
      <div className={css.skAssessmentGrid}>
        <Bone className={css.skAssessment} />
        <Bone className={css.skAssessment} />
      </div>
    </div>
  );
}

function ProfilePanelSkeleton() {
  return (
    <>
      {/* Profile part */}
      <div className={css.skProfilePart}>
        <Bone className={css.skText} style={{ width: 60, alignSelf: "flex-start" }} />
        {/* Avatar circle */}
        <Bone
          className={`${css.skeleton} ${css.skCircle}`}
          style={{ width: 108, height: 108 }}
        />
        {/* Name */}
        <Bone className={css.skTitle} style={{ width: 120 }} />
        {/* Class + level chip */}
        <div style={{ display: "flex", gap: "0.8rem", alignItems: "center" }}>
          <Bone className={css.skText} style={{ width: 80 }} />
          <Bone className={css.skText} style={{ width: 60, borderRadius: 20 }} />
        </div>
      </div>

      {/* Profile detail */}
      <div className={css.skProfileDetail}>
        {/* Weekly Activity heading */}
        <Bone className={css.skTitle} style={{ width: 160 }} />
        {/* Chart */}
        <Bone className={css.skChart} />

        {/* Mentors heading */}
        <Bone className={css.skTitle} style={{ width: 180, marginTop: "0.4rem" }} />
        {/* Teacher rows × 3 */}
        {[1, 2, 3].map((i) => (
          <div key={i} className={css.skTeacherRow}>
            <Bone
              className={`${css.skeleton} ${css.skCircle}`}
              style={{ width: 40, height: 40 }}
            />
            <div className={css.skTeacherDetails}>
              <Bone className={css.skText} style={{ width: "60%" }} />
              <Bone className={css.skText} style={{ width: "45%" }} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ── Root skeleton ──────────────────────────────────────────── */

function DashboardSkeleton({ showSidebar = true }) {
  return (
    <div className={css.skWrap}>
      {/* Left side placeholder (mirrors .side_wrap — empty in current dashboard) */}
      <div style={{ width: 0 }} />

      {/* Main content column */}
      <div className={css.skContentWrap}>
        <TitleBarSkeleton />
        <CarouselSkeleton />
        <InfoCardsSkeleton />
        <TabSectionSkeleton courseCount={3} />
        <AssessmentSkeleton />
        <TabSectionSkeleton courseCount={3} />
      </div>

      {/* Right profile panel */}
      {showSidebar && (
        <div className={css.skProfileWrap}>
          <ProfilePanelSkeleton />
        </div>
      )}
    </div>
  );
}

export default DashboardSkeleton;
